import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaLaptopCode, FaPlus, FaSave, FaTrash, FaEdit } from 'react-icons/fa'; // Added FaLaptopCode for icon


const ClientDetails = () => {
    const { clientId } = useParams();
    const navigate = useNavigate();
    const [client, setClient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [socialMediaAccounts, setSocialMediaAccounts] = useState([]);
    const [isEditingSocialMedia, setIsEditingSocialMedia] = useState(false);

    const [websiteCredentials, setWebsiteCredentials] = useState([]);
    const [isEditingWebsite, setIsEditingWebsite] = useState(false);

    const [selectedFile, setSelectedFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [uploadingLogo, setUploadingLogo] = useState(false);

    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

    // Base URL for public files (no trailing slash)
    const contractsBase = String(BACKEND_URL || '').replace(/\/$/, '');

    useEffect(() => {
        console.log("ClientDetails component mounted. Client ID:", clientId);

        const fetchClientDetails = async () => {
            setLoading(true);
            setError(null);
            const token = localStorage.getItem("token");

            if (!token) {
                console.error("No authentication token found.");
                setError("Authentication required. Please log in.");
                setLoading(false);
                toast.error("Authentication failed. Please log in.");
                return;
            }

            try {
                console.log(`Attempting to fetch client with ID: ${clientId}`);
                const res = await axios.get(`${BACKEND_URL}/api/client/${clientId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                    withCredentials: true,
                });
                console.log("Client data fetched successfully:", res.data);
                if (res.data) {
                    setClient(res.data);
                    setSocialMediaAccounts(res.data.socialMediaAccounts || []);
                    setWebsiteCredentials(res.data.websiteCredentials || []);

                    // Fix 1: Ensure there is a '/' between the URL and the path
                    if (res.data.logo) {
                        setLogoPreview(`${BACKEND_URL}/${res.data.logo}`);
                    } else {
                        setLogoPreview(null);
                    }
                } else {
                    console.warn("API returned no data for this client ID.");
                    setError("Client data not found for this ID.");
                }
                setLoading(false);
            } catch (err) {
                console.error("Error fetching client details:", err.response?.data || err.message);
                setError(`Failed to fetch client details: ${err.response?.data?.error || err.message}`);
                toast.error("Failed to load client details.");
                setLoading(false);
            }
        };

        if (clientId) {
            fetchClientDetails();
        } else {
            console.warn("No client ID found in URL parameters.");
            setError("No client ID provided in the URL.");
            setLoading(false);
        }
    }, [clientId, BACKEND_URL]);

    // Generate contract for this client, download and refresh client data
    const generateContract = async () => {
        if (!clientId) {
            toast.error('Missing client id');
            return;
        }
        const token = localStorage.getItem('token');
        try {
            const resp = await axios.get(`${BACKEND_URL}/api/client/${encodeURIComponent(clientId)}/create-contract`, {
                responseType: 'blob',
                headers: { Authorization: `Bearer ${token}` },
                withCredentials: true,
            });

            // trigger download
            const blob = new Blob([resp.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const safeName = (client && client.clientName) ? String(client.clientName).replace(/[^a-z0-9-_\.]/gi, '_') : `client_${clientId}`;
            a.download = `${safeName}_contract.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);

            toast.success('Contract generated');

            // refresh client details so contracts list updates
            try {
                const fresh = await axios.get(`${BACKEND_URL}/api/client/${clientId}`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
                if (fresh && fresh.data) setClient(fresh.data);
            } catch (refreshErr) {
                console.error('Failed to refresh client after contract generation', refreshErr);
            }
        } catch (err) {
            console.error('Error generating contract:', err);
            toast.error('Failed to generate contract');
        }
    };

    // This handler is correct, no changes needed here.
    const handleFileChange = (event) => {
        const file = event.target.files[0];
        setSelectedFile(file);
        if (file) {
            setLogoPreview(URL.createObjectURL(file));
        } else {
            setLogoPreview(client?.logo ? `${BACKEND_URL}/${client.logo}` : null);
        }
    };

    const handleLogoUpload = async () => {
        if (!selectedFile) {
            toast.warn("Please select an image file to upload.");
            return;
        }

        setUploadingLogo(true);
        const token = localStorage.getItem("token");
        const formData = new FormData();
        formData.append('logo', selectedFile);

        try {
            const response = await axios.post(
                `${BACKEND_URL}/api/client/${clientId}/upload-logo`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data',
                    },
                    withCredentials: true,
                }
            );
            toast.success("Client logo uploaded successfully!");
            setClient(prevClient => ({
                ...prevClient,
                logo: response.data.logo
            }));
            setSelectedFile(null);
            // Fix 2: Ensure the '/' is added here as well
            setLogoPreview(`${BACKEND_URL}/${response.data.logo}`);
        } catch (error) {
            console.error("Error uploading logo:", error.response?.data || error.message);
            toast.error("Failed to upload logo: " + (error.response?.data?.error || "Unknown error"));
        } finally {
            setUploadingLogo(false);
        }
    };

    // All other handlers and the return statement remain the same as you provided them
    const handleSocialMediaChange = (e, index) => {
      const { name, value } = e.target;
      const list = [...socialMediaAccounts];
      list[index][name] = value;
      setSocialMediaAccounts(list);
    };

    const handleAddSocialMedia = () => {
      setSocialMediaAccounts([...socialMediaAccounts, { platform: "", username: "", password: "" }]);
    };

    const handleRemoveSocialMedia = (index) => {
      const list = [...socialMediaAccounts];
      list.splice(index, 1);
      setSocialMediaAccounts(list);
    };

    const handleSaveSocialMedia = async () => {
      const token = localStorage.getItem("token");
      try {
        const filteredAccounts = socialMediaAccounts.filter(
          (acc) => acc.platform || acc.username || acc.password
        );
        await axios.put(
          `${BACKEND_URL}/api/client/${clientId}`,
          { socialMediaAccounts: filteredAccounts },
          {
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            withCredentials: true,
          }
        );
        toast.success("Social media accounts updated successfully!");
        setClient(prevClient => ({
          ...prevClient,
          socialMediaAccounts: filteredAccounts
        }));
        setIsEditingSocialMedia(false);
      } catch (error) {
        console.error("Error updating social media accounts:", error.response?.data || error.message);
        toast.error("Failed to update social media accounts.");
      }
    };

    const handleWebsiteChange = (e, index) => {
      const { name, value } = e.target;
      const list = [...websiteCredentials];
      list[index][name] = value;
      setWebsiteCredentials(list);
    };

    const handleAddWebsite = () => {
      setWebsiteCredentials([...websiteCredentials, { siteUrl: "", username: "", appPassword: "" }]);
    };

    const handleRemoveWebsite = (index) => {
      const list = [...websiteCredentials];
      list.splice(index, 1);
      setWebsiteCredentials(list);
    };

    const handleSaveWebsite = async () => {
      const token = localStorage.getItem("token");
      try {
        const filteredCredentials = websiteCredentials.filter(
          (cred) => cred.siteUrl || cred.username || cred.appPassword
        );
        await axios.put(
          `${BACKEND_URL}/api/client/${clientId}`,
          { websiteCredentials: filteredCredentials },
          {
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            withCredentials: true,
          }
        );
        toast.success("Website credentials updated successfully!");
        setClient(prevClient => ({
          ...prevClient,
          websiteCredentials: filteredCredentials
        }));
        setIsEditingWebsite(false);
      } catch (error) {
        console.error("Error updating website credentials:", error.response?.data || error.message);
        toast.error("Failed to update website credentials.");
      }
    };


    if (loading) {
        return (
            <div className="p-6 flex items-center justify-center h-screen text-white text-xl">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mr-3"></div>
                Loading client details...
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6 text-center text-red-400 text-lg">
                <p>Error: {error}</p>
                <button
                    onClick={() => navigate('/DashBoard')}
                    className="mt-4 bg-gray-700 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition duration-200"
                >
                    Back to Clients
                </button>
            </div>
        );
    }

    if (!client) {
        return (
            <div className="p-6 text-center text-gray-400 text-lg">
                <p>Client not found or data is empty.</p>
                <button
                    onClick={() => navigate('/DashBoard')}
                    className="mt-4 bg-gray-700 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition duration-200"
                >
                    Back to Clients
                </button>
            </div>
        );
    }
    

    return (
        <div className="p-6 font-sans">
            <button
                onClick={() => navigate('/DashBoard')}
                className="mb-6 bg-gray-700 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition duration-200 flex items-center gap-2"
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Back to Clients
            </button>
            
            <div className="bg-white rounded-xl shadow-lg p-8 max-w-4xl mx-auto border border-gray-200">
                {/* Client Name and Logo Display */}
                <div className="flex flex-col md:flex-row items-center md:items-start gap-6 mb-6 pb-6 border-b-2 border-gray-200">
                    <div className="flex-shrink-0 w-32 h-32 rounded-full overflow-hidden border-2 border-gray-300 bg-gray-100 flex items-center justify-center">
                        {logoPreview ? (
                            <img src={logoPreview} alt="Client Logo" className="w-full h-full object-cover" 
                                onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/128x128/e5e7eb/6b7280?text=Logo+Error"; }}
                            />
                        ) : (
                            <span className="text-gray-400 text-6xl">👤</span>
                        )}
                    </div>
                    <div className="flex-1 text-center md:text-left">
                        <h1 className="text-4xl font-bold text-gray-900">
                            {client.clientName}
                        </h1>
                        <p className="text-gray-600 mt-1">{client.company || 'No Company'}</p>
                    </div>
                </div>

                    <div className="flex justify-end mb-6">
                        <button
                            onClick={generateContract}
                            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition duration-200 flex items-center gap-2"
                        >
                            <FaLaptopCode /> Generate Contract
                        </button>
                    </div>

                {/* Logo Upload Section */}
                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">Upload Client Logo</h2>
                    <div className="flex flex-col md:flex-row items-center gap-4">
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileChange}
                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                        <button
                            onClick={handleLogoUpload}
                            disabled={!selectedFile || uploadingLogo}
                            className="min-w-[120px] bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                        >
                            {uploadingLogo ? (
                                <svg className="animate-spin h-5 w-5 text-white mr-2" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                            ) : (
                                'Upload Logo'
                            )}
                        </button>
                    </div>
                </section>

                {/* General Details */}
                <section className="mb-8 border-t pt-6 border-gray-200">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4">General Information</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-gray-700">
                        <p><span className="font-semibold">Company:</span> {client.company || 'N/A'}</p>
                        <p><span className="font-semibold">Group:</span> {client.group || 'N/A'}</p>
                        <p><span className="font-semibold">Email:</span> {client.Email || 'N/A'}</p>
                        <p><span className="font-semibold">Contact:</span> {client.contact || 'N/A'}</p>
                        <p><span className="font-semibold">Alternate Contact:</span> {client.contactalt || 'N/A'}</p>
                    </div>
                </section>

                {/* Address */}
                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4 border-t pt-6 border-gray-200">Address Details</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-gray-700">
                        <p><span className="font-semibold">Building:</span> {client.building}</p>
                        <p><span className="font-semibold">Street:</span> {client.street}</p>
                        <p><span className="font-semibold">City:</span> {client.city}</p>
                        <p><span className="font-semibold">District:</span> {client.district}</p>
                        <p><span className="font-semibold">State:</span> {client.state}</p>
                        <p><span className="font-semibold">Pincode:</span> {client.pincode}</p>
                         <p><span className="font-semibold">field:</span> {client.field}</p>
                        <p><span className="font-semibold">description:</span> {client.description}</p>   
                    </div>
                </section>

                {/* Contracts Section */}
                <section className="mb-8">
                    <h2 className="text-2xl font-semibold text-gray-800 mb-4 border-t pt-6 border-gray-200">Contracts</h2>
                    {client.contracts && client.contracts.length > 0 ? (
                        <div className="space-y-3">
                            {client.contracts.map((c, idx) => (
                                <div key={idx} className="flex items-center justify-between bg-gray-50 p-3 rounded-md border">
                                    <div>
                                        <div className="text-sm font-medium text-gray-800">{c.fileName}</div>
                                        <div className="text-xs text-gray-500">{new Date(c.createdAt).toLocaleString()}</div>
                                    </div>
                                    <div className="flex gap-2">
                                        <a
                                            href={`${contractsBase}${c.publicPath}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
                                        >
                                            View
                                        </a>
                                        <a
                                            href={`${contractsBase}${c.publicPath}`}
                                            download
                                            className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                                        >
                                            Download
                                        </a>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-gray-500 italic">No contracts generated for this client yet.</p>
                    )}
                </section>

                {/* Social Media Accounts - Display and Edit */}
                <section className="mb-8">
                    <div className="flex justify-between items-center mb-4 border-t pt-6 border-gray-200">
                        <h2 className="text-2xl font-semibold text-gray-800">Social Media Accounts (Credential Wallet)</h2>
                        <button
                            onClick={() => setIsEditingSocialMedia(!isEditingSocialMedia)}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition duration-200 ${
                                isEditingSocialMedia ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'
                            }`}
                        >
                            {isEditingSocialMedia ? 'Cancel Edit' : 'Edit Accounts'}
                        </button>
                    </div>

                    {isEditingSocialMedia ? (
                        <div className="space-y-6">
                            {socialMediaAccounts.length > 0 ? (
                                socialMediaAccounts.map((account, index) => (
                                    <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 border rounded-md bg-white shadow-sm">
                                        <div>
                                            <label htmlFor={`platform-${index}`} className="block text-sm font-medium mb-1 text-gray-700">Platform</label>
                                            <input
                                                type="text"
                                                id={`platform-${index}`}
                                                name="platform"
                                                value={account.platform}
                                                onChange={(e) => handleSocialMediaChange(e, index)}
                                                required
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 transition duration-200"
                                                placeholder="e.g., Facebook, Instagram"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor={`username-${index}`} className="block text-sm font-medium mb-1 text-gray-700">Username</label>
                                            <input
                                                type="text"
                                                id={`username-${index}`}
                                                name="username"
                                                value={account.username}
                                                onChange={(e) => handleSocialMediaChange(e, index)}
                                                required
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 transition duration-200"
                                                placeholder="Username"
                                            />
                                        </div>
                                        <div className="flex items-end gap-2">
                                            <div className="flex-grow">
                                                <label htmlFor={`password-${index}`} className="block text-sm font-medium mb-1 text-gray-700">Password</label>
                                                <input
                                                    type="password"
                                                    id={`password-${index}`}
                                                    name="password"
                                                    value={account.password}
                                                    onChange={(e) => handleSocialMediaChange(e, index)}
                                                    required
                                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 transition duration-200"
                                                    placeholder="Password"
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveSocialMedia(index)}
                                                className="p-2 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition duration-200 flex-shrink-0"
                                                title="Remove Account"
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="text-gray-500 italic">No social media accounts to edit. Click "Add New Account".</p>
                            )}
                            
                            <button
                                type="button"
                                onClick={handleAddSocialMedia}
                                className="mt-4 px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition duration-200 flex items-center gap-2"
                            >
                                <FaPlus /> Add New Account
                            </button>

                            <button
                                type="button"
                                onClick={handleSaveSocialMedia}
                                className="mt-6 w-full bg-green-600 text-white px-4 py-3 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition duration-200 text-lg font-medium shadow-md"
                            >
                                <FaSave /> Save Social Media Changes
                            </button>
                        </div>
                    ) : (
                        // Display mode for social media accounts
                        socialMediaAccounts && socialMediaAccounts.length > 0 ? (
                            <div className="space-y-4">
                                {socialMediaAccounts.map((account, index) => (
                                    <div key={index} className="bg-gray-100 p-4 rounded-lg shadow-sm border border-gray-200">
                                        <p className="text-lg font-medium text-gray-800"><span className="font-bold">Platform:</span> {account.platform}</p>
                                        <p className="text-gray-700"><span className="font-semibold">Username:</span> {account.username}</p>
                                        <p className="text-gray-700">
                                            <span className="font-semibold">Password:</span>
                                            <span className="ml-2 font-mono tracking-wider">{account.password}</span>
                                        </p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-gray-500 italic">No social media accounts added for this client.</p>
                        )
                    )}
                </section>

                {/* --- New Section for Website Credentials --- */}
                <section>
                    <div className="flex justify-between items-center mb-4 border-t pt-6 border-gray-200">
                        <h2 className="text-2xl font-semibold text-gray-800">Website Credentials</h2>
                        <button
                            onClick={() => setIsEditingWebsite(!isEditingWebsite)}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition duration-200 ${
                                isEditingWebsite ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'
                            }`}
                        >
                            <FaEdit className="inline mr-2" /> {isEditingWebsite ? 'Cancel Edit' : 'Edit Credentials'}
                        </button>
                    </div>

                    {isEditingWebsite ? (
                        <div className="space-y-6">
                            {websiteCredentials.length > 0 ? (
                                websiteCredentials.map((credential, index) => (
                                    <div key={index} className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 border rounded-md bg-white shadow-sm">
                                        <div>
                                            <label htmlFor={`siteUrl-${index}`} className="block text-sm font-medium mb-1 text-gray-700">Site URL</label>
                                            <input
                                                type="text"
                                                id={`siteUrl-${index}`}
                                                name="siteUrl"
                                                value={credential.siteUrl}
                                                onChange={(e) => handleWebsiteChange(e, index)}
                                                required
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 transition duration-200"
                                                placeholder="e.g., https://example.com"
                                            />
                                        </div>
                                        <div>
                                            <label htmlFor={`websiteUsername-${index}`} className="block text-sm font-medium mb-1 text-gray-700">Username</label>
                                            <input
                                                type="text"
                                                id={`websiteUsername-${index}`}
                                                name="username"
                                                value={credential.username}
                                                onChange={(e) => handleWebsiteChange(e, index)}
                                                required
                                                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 transition duration-200"
                                                placeholder="Username"
                                            />
                                        </div>
                                        <div className="flex items-end gap-2">
                                            <div className="flex-grow">
                                                <label htmlFor={`appPassword-${index}`} className="block text-sm font-medium mb-1 text-gray-700">App Password</label>
                                                <input
                                                    type="password"
                                                    id={`appPassword-${index}`}
                                                    name="appPassword"
                                                    value={credential.appPassword}
                                                    onChange={(e) => handleWebsiteChange(e, index)}
                                                    required
                                                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-2 focus:ring-blue-500 transition duration-200"
                                                    placeholder="Password"
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveWebsite(index)}
                                                className="p-2 bg-red-500 text-white rounded-md hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition duration-200 flex-shrink-0"
                                                title="Remove Credential"
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p className="text-gray-500 italic">No website credentials to edit. Click "Add New Credential".</p>
                            )}
                            
                            <button
                                type="button"
                                onClick={handleAddWebsite}
                                className="mt-4 px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition duration-200 flex items-center gap-2"
                            >
                                <FaPlus /> Add New Credential
                            </button>

                            <button
                                type="button"
                                onClick={handleSaveWebsite}
                                className="mt-6 w-full bg-green-600 text-white px-4 py-3 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition duration-200 text-lg font-medium shadow-md"
                            >
                                <FaSave /> Save Website Changes
                            </button>
                        </div>
                    ) : (
                        // Display mode for website credentials
                        websiteCredentials && websiteCredentials.length > 0 ? (
                            <div className="space-y-4">
                                {websiteCredentials.map((credential, index) => (
                                    <div key={index} className="bg-gray-100 p-4 rounded-lg shadow-sm border border-gray-200">
                                        <p className="text-lg font-medium text-gray-800"><span className="font-bold">Site URL:</span> {credential.siteUrl}</p>
                                        <p className="text-gray-700"><span className="font-semibold">Username:</span> {credential.username}</p>
                                        <p className="text-gray-700">
                                            <span className="font-semibold">App Password:</span>
                                            <span className="ml-2 font-mono tracking-wider">{credential.appPassword}</span>
                                        </p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-gray-500 italic">No website credentials added for this client.</p>
                        )
                    )}
                </section>
            </div>
        </div>
    );
};

export default ClientDetails;

